import { useState } from 'react';
import { DollarSign, Receipt, TrendingUp, Calculator, PiggyBank, Users, Target, Megaphone, MapPin, Crown, CreditCard, Wallet, Banknote, Building2, CircleDollarSign, CheckCircle2, Globe } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { MetricCard, MetricComparison } from './MetricCard';
import { RevenueBreakdown } from './RevenueBreakdown';
import { CostBreakdown } from './CostBreakdown';
import { LeadCohortMetrics } from './LeadCohortMetrics';
import { JobScheduleMetrics } from './JobScheduleMetrics';
import { ReconciliationPanel } from './ReconciliationPanel';
import { PaymentJobsSheet } from './finance/PaymentJobsSheet';
import { useOverallReport } from '@/hooks/useOverallReport';
import { useUnifiedDashboard } from '@/hooks/useUnifiedDashboard';
import { useComparisonMetrics, ComparisonMetrics } from '@/hooks/useComparisonMetrics';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { formatTaxLine, formatTaxSuffix } from '@/utils/tax';
import { calculateChange } from '@/utils/periodComparison';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useCompanyExpenses } from '@/hooks/useCompanyExpenses';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface OverallPerformanceProps {
  startDate: Date;
  endDate: Date;
  timezone?: string;
  compareEnabled?: boolean;
}

export const OverallPerformance = ({ startDate, endDate, timezone: propTimezone, compareEnabled = false }: OverallPerformanceProps) => {
  const { timezone: contextTimezone } = useTimezoneUtils();
  const timezone = propTimezone || contextTimezone;
  const [activePaymentSheet, setActivePaymentSheet] = useState<'other' | null>(null);

  const { data: metrics, isLoading: metricsLoading, error: metricsError } = useOverallReport(startDate, endDate, timezone);
  const { data: dashboard, isLoading: dashboardLoading, error: dashboardError } = useUnifiedDashboard(startDate, endDate, timezone);
  const { activeExpenses, filterExpensesByPeriod, calculateMonthlyAmount } = useCompanyExpenses();
  const tax = useCompanyTax();
  const incSuffix = tax.enabled ? ` (${formatTaxSuffix(tax)})` : '';
  const exSuffix = tax.enabled ? ` (ex. ${tax.label})` : '';

  // Calculate total operating expenses for the selected period
  const filteredExpenses = filterExpensesByPeriod(activeExpenses, startDate, endDate);
  const totalOperatingExpenses = filteredExpenses.reduce((sum, expense) => {
    if (expense.frequency === 'one_time') {
      return sum + expense.amount;
    }
    return sum + calculateMonthlyAmount(expense);
  }, 0);

  // PERFORMANCE: Comparison data loads AFTER main data AND only when comparison is enabled (deferred loading)
  const mainDataLoaded = !metricsLoading && !dashboardLoading && !!metrics && !!dashboard;
  const { data: comparisonData, isLoading: comparisonLoading } = useComparisonMetrics(
    startDate,
    endDate,
    timezone,
    mainDataLoaded && compareEnabled
  );

  const { formatMoney: formatCurrency, locale } = useCompanyLocale();
  // Abbreviated axis ticks ("$12K") in the tenant's currency
  const formatCompactMoney = (amount: number) =>
    new Intl.NumberFormat(locale.locale, {
      style: 'currency',
      currency: locale.currency,
      notation: 'compact',
      maximumFractionDigits: 0,
    }).format(amount);

  // Helper to build comparison prop for a metric
  const getComparison = (
    current: number,
    previousKey: keyof ComparisonMetrics,
    isPositiveGood = true
  ): MetricComparison | undefined => {
    if (!comparisonData) return undefined;
    const change = calculateChange(current, comparisonData.metrics[previousKey]);
    return {
      ...change,
      label: comparisonData.period.label,
      isPositiveGood
    };
  };

  const isLoading = metricsLoading || dashboardLoading;
  const error = metricsError || dashboardError;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[...Array(7)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[...Array(7)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load report data. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  if (!metrics || !dashboard) return null;

  const revenueBreakdownItems = [
    { label: `Total Invoiced${incSuffix}`, amount: metrics.totalInvoiced },
    { label: `Collected Revenue${incSuffix}`, amount: metrics.totalTurnover, isBold: true },
    ...(tax.enabled
      ? [
          { label: `Less: ${tax.label} on Collected`, amount: metrics.gstOwed, isNegative: true },
          { label: `Gross Revenue${exSuffix}`, amount: metrics.turnoverAfterTax, isBold: true },
        ]
      : []),
    { label: 'Average Job Value', amount: metrics.jobsCompleted > 0 ? metrics.totalTurnover / metrics.jobsCompleted : 0 },
  ];

  const costBreakdownItems = [
    { label: 'Parts Cost', amount: metrics.partsCost, isNegative: true },
    { label: 'Toll Costs', amount: metrics.tollCost, isNegative: true },
    { label: 'Card Processing Fees', amount: metrics.cardProcessingFees, isNegative: true },
    { label: 'Technician Payouts', amount: metrics.techPay, isNegative: true },
    { label: 'Managers Commission', amount: metrics.managersCommission, isNegative: true },
    { label: `Campaign/Ad Spend${incSuffix}`, amount: metrics.campaignCost, isNegative: true },
    { label: 'Operating Expenses', amount: totalOperatingExpenses, isNegative: true },
    { label: 'Net Company Profit', amount: metrics.netCompanyProfit - totalOperatingExpenses, isBold: true },
  ];

  const netProfitAfterExpenses = metrics.netCompanyProfit - totalOperatingExpenses;

  const leadStatusRows = [
    { label: 'New', count: dashboard.leadMetrics.openLeads, barClass: 'bg-info' },
    { label: 'Follow-up', count: dashboard.leadMetrics.followUpLeads, barClass: 'bg-warning' },
    { label: 'Converted', count: dashboard.leadMetrics.convertedLeads, barClass: 'bg-success' },
    { label: 'Lost', count: dashboard.leadMetrics.lostLeads, barClass: 'bg-destructive' },
  ];
  const totalLeadCount = dashboard.leadMetrics.totalLeads;

  return (
    <div className="space-y-6">
      {/* Headline KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Collected Revenue"
          value={formatCurrency(metrics.totalTurnover)}
          subtitle={tax.enabled ? `Including ${tax.label}` : 'Total collected'}
          icon={DollarSign}
          valueColor="text-primary"
          iconBgColor="bg-primary/10"
          iconColor="text-primary"
          comparison={getComparison(metrics.totalTurnover, 'totalTurnover', true)}
          comparisonLoading={comparisonLoading}
        />
        <MetricCard
          title="Total Leads"
          value={totalLeadCount}
          subtitle="Received in period"
          icon={Users}
          valueColor="text-info"
          iconBgColor="bg-info/10"
          iconColor="text-info"
          comparison={getComparison(totalLeadCount, 'totalLeads', true)}
          comparisonLoading={comparisonLoading}
        />
        <MetricCard
          title="Jobs Completed"
          value={metrics.jobsCompleted}
          subtitle="Closed in period"
          icon={CheckCircle2}
          valueColor="text-success"
          iconBgColor="bg-success/10"
          iconColor="text-success"
          comparison={getComparison(metrics.jobsCompleted, 'completedJobs', true)}
          comparisonLoading={comparisonLoading}
        />
        <MetricCard
          title="Net Profit"
          value={formatCurrency(netProfitAfterExpenses)}
          subtitle="After all costs & expenses"
          icon={PiggyBank}
          valueColor={netProfitAfterExpenses >= 0 ? 'text-success' : 'text-destructive'}
          iconBgColor={netProfitAfterExpenses >= 0 ? 'bg-success/10' : 'bg-destructive/10'}
          iconColor={netProfitAfterExpenses >= 0 ? 'text-success' : 'text-destructive'}
          comparison={getComparison(netProfitAfterExpenses, 'netProfit', true)}
          comparisonLoading={comparisonLoading}
        />
      </div>

      {/* Revenue chart + Leads by status */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[3fr_2fr]">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Revenue by metro</CardTitle>
          </CardHeader>
          <CardContent>
            {metrics.metroPerformance.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No revenue data available.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={metrics.metroPerformance} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="metro_name"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => formatCompactMoney(v)}
                  />
                  <RechartsTooltip
                    formatter={(value: number) => [formatCurrency(value), 'Turnover']}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      color: 'hsl(var(--popover-foreground))',
                      fontSize: '12px',
                    }}
                    cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                  />
                  <Bar dataKey="turnover" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Leads by status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {leadStatusRows.map(({ label, count, barClass }) => {
              const pct = totalLeadCount > 0 ? (count / totalLeadCount) * 100 : 0;
              return (
                <div key={label}>
                  <div className="mb-1.5 flex items-center justify-between text-[13px]">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-medium tabular-nums">
                      {count} <span className="font-normal text-muted-foreground">· {pct.toFixed(0)}%</span>
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div className={`h-full rounded-full ${barClass}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {totalLeadCount === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">No leads in this period.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* TODO(csr-report rebuild): Answer % was removed from the CSR tab because a shared
          queue has no per-agent answer rate (an unanswered call has no agent). It belongs
          here as an office-level number: inbound call_logs in range, disposition_bucket =
          'answered' ÷ total inbound. Not built yet. */}
      {/* TODO(csr-report rebuild): Recovered Revenue (jobs.invoice_total by job_date for
          leads whose job was created after a "-> lost" marking; see
          get_csr_lead_cohort.recovered) was removed from the CSR tab. It is a revenue
          metric and belongs here. Not built yet. */}
      {/* TODO(csr-report rebuild): "Lost to no tech availability" (leads.lost_reason
          ILIKE 'no tech available%', class = 'lost_ops' in get_csr_lead_cohort) is an
          operations metric, not a CSR metric. Surface it here. Not built yet. */}

      {/* Lead-Based Metrics Section */}
      <LeadCohortMetrics
        metrics={dashboard.leadMetrics}
        comparisonMetrics={comparisonData?.metrics}
        comparisonLabel={comparisonData?.period.label}
        comparisonLoading={comparisonLoading}
      />

      <Separator />

      {/* Job-Based Metrics Section */}
      <JobScheduleMetrics
        metrics={dashboard.jobMetrics}
        comparisonMetrics={comparisonData?.metrics}
        comparisonLabel={comparisonData?.period.label}
        comparisonLoading={comparisonLoading}
      />

      <Separator />

      {/* Reconciliation Panel */}
      <ReconciliationPanel
        leadMetrics={dashboard.leadMetrics}
        jobMetrics={dashboard.jobMetrics}
        reconciliation={dashboard.reconciliation}
      />

      <Separator />

      {/* Metro Performance */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Metro Performance
        </h3>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metro</TableHead>
                  <TableHead className="text-right">Leads</TableHead>
                  <TableHead className="text-right">Jobs</TableHead>
                  <TableHead className="text-right">Turnover</TableHead>
                  <TableHead className="text-right">Ad Spend</TableHead>
                  <TableHead className="text-right">Net Profit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics.metroPerformance.map((metro) => (
                  <TableRow key={metro.metro_id}>
                    <TableCell className="font-medium">{metro.metro_name}</TableCell>
                    <TableCell className="text-right">{metro.leads_count}</TableCell>
                    <TableCell className="text-right">{metro.jobs_count}</TableCell>
                    <TableCell className="text-right">{formatCurrency(metro.turnover)}</TableCell>
                    <TableCell className="text-right text-destructive">
                      {formatCurrency(metro.campaign_cost)}
                    </TableCell>
                    <TableCell className={`text-right font-medium tabular-nums ${metro.net_profit >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {formatCurrency(metro.net_profit)}
                    </TableCell>
                  </TableRow>
                ))}
                {metrics.metroPerformance.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No metro data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Financial Metrics */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Financial Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Turnover"
            value={formatCurrency(metrics.totalTurnover)}
            subtitle={tax.enabled ? `Including ${tax.label}` : 'Total collected'}
            icon={DollarSign}
            valueColor="text-primary"
            iconBgColor="bg-primary/10"
            comparison={getComparison(metrics.totalTurnover, 'totalTurnover', true)}
            comparisonLoading={comparisonLoading}
          />

          {tax.enabled && (
            <MetricCard
              title={`Tax (${tax.label})`}
              value={formatCurrency(metrics.gstOwed)}
              subtitle={formatTaxLine(tax)}
              icon={Receipt}
              valueColor="text-destructive"
              iconBgColor="bg-destructive/10"
              iconColor="text-destructive"
              comparison={getComparison(metrics.gstOwed, 'gstOwed', false)}
              comparisonLoading={comparisonLoading}
            />
          )}

          <MetricCard
            title="Turnover After Tax"
            value={formatCurrency(metrics.turnoverAfterTax)}
            subtitle={tax.enabled ? `Revenue ex. ${tax.label}` : 'Net revenue'}
            icon={TrendingUp}
            valueColor="text-primary"
            iconBgColor="bg-primary/10"
            comparison={getComparison(metrics.turnoverAfterTax, 'totalTurnover', true)}
            comparisonLoading={comparisonLoading}
          />

          <MetricCard
            title="Tech Pay"
            value={formatCurrency(metrics.techPay)}
            subtitle="Commission paid"
            icon={Users}
            valueColor="text-warning"
            iconBgColor="bg-warning/10"
            iconColor="text-warning"
            comparison={getComparison(metrics.techPay, 'techPay', false)}
            comparisonLoading={comparisonLoading}
          />

          <MetricCard
            title="Parts Cost"
            value={formatCurrency(metrics.partsCost)}
            subtitle="Total parts"
            icon={Calculator}
            valueColor="text-destructive"
            iconBgColor="bg-destructive/10"
            iconColor="text-destructive"
            comparison={getComparison(metrics.partsCost, 'partsCost', false)}
            comparisonLoading={comparisonLoading}
          />

          <MetricCard
            title="Campaign Cost"
            value={formatCurrency(metrics.campaignCost)}
            subtitle="Ad spend (inc. tax)"
            icon={Megaphone}
            valueColor="text-destructive"
            iconBgColor="bg-destructive/10"
            iconColor="text-destructive"
            comparison={getComparison(metrics.campaignCost, 'campaignCost', false)}
            comparisonLoading={comparisonLoading}
          />

          <MetricCard
            title="Card Processing Fees"
            value={formatCurrency(metrics.cardProcessingFees)}
            subtitle="Card/EFTPOS fees"
            icon={CreditCard}
            valueColor="text-warning"
            iconBgColor="bg-warning/10"
            iconColor="text-warning"
          />

          <MetricCard
            title="Managers Commission"
            value={formatCurrency(metrics.managersCommission)}
            subtitle="Override payouts"
            icon={Crown}
            valueColor="text-primary"
            iconBgColor="bg-primary/10"
            iconColor="text-primary"
          />

          <MetricCard
            title="Operating Expenses"
            value={formatCurrency(totalOperatingExpenses)}
            subtitle="Monthly recurring + one-time"
            icon={Wallet}
            valueColor="text-destructive"
            iconBgColor="bg-destructive/10"
            iconColor="text-destructive"
          />

          <MetricCard
            title="Net Company Profit"
            value={formatCurrency(metrics.netCompanyProfit - totalOperatingExpenses)}
            subtitle="After all costs incl. ads & expenses"
            icon={PiggyBank}
            valueColor={(metrics.netCompanyProfit - totalOperatingExpenses) >= 0 ? "text-success" : "text-destructive"}
            iconBgColor={(metrics.netCompanyProfit - totalOperatingExpenses) >= 0 ? "bg-success/10" : "bg-destructive/10"}
            iconColor={(metrics.netCompanyProfit - totalOperatingExpenses) >= 0 ? "text-success" : "text-destructive"}
            comparison={getComparison(metrics.netCompanyProfit - totalOperatingExpenses, 'netProfit', true)}
            comparisonLoading={comparisonLoading}
          />

          <MetricCard
            title="Avg Job Profit"
            value={formatCurrency(metrics.avgJobProfit)}
            subtitle="Per completed job"
            icon={Target}
            valueColor="text-success"
            iconBgColor="bg-success/10"
            iconColor="text-success"
          />
        </div>
      </div>

      <Separator />

      {/* Payment Methods */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Payment Methods
        </h3>
        {(() => {
          const breakdown = metrics.paymentMethodBreakdown;
          const totalCollected = breakdown.cash + breakdown.card + breakdown.bank_transfer + (breakdown.square ?? 0) + breakdown.other;
          const getPercentage = (amount: number) => totalCollected > 0 ? ((amount / totalCollected) * 100).toFixed(1) : '0.0';

          return (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MetricCard
                title="Cash"
                value={formatCurrency(breakdown.cash)}
                subtitle={`${getPercentage(breakdown.cash)}% of collected`}
                icon={Banknote}
                valueColor="text-success"
                iconBgColor="bg-success/10"
                iconColor="text-success"
              />
              <MetricCard
                title="Card/EFTPOS"
                value={formatCurrency(breakdown.card)}
                subtitle={`${getPercentage(breakdown.card)}% of collected`}
                icon={CreditCard}
                valueColor="text-info"
                iconBgColor="bg-info/10"
                iconColor="text-info"
              />
              <MetricCard
                title="Bank Transfer"
                value={formatCurrency(breakdown.bank_transfer)}
                subtitle={`${getPercentage(breakdown.bank_transfer)}% of collected`}
                icon={Building2}
                valueColor="text-primary"
                iconBgColor="bg-primary/10"
                iconColor="text-primary"
              />
              <MetricCard
                title="Square (online)"
                value={formatCurrency(breakdown.square ?? 0)}
                subtitle={`${getPercentage(breakdown.square ?? 0)}% of collected`}
                icon={Globe}
                valueColor="text-info"
                iconBgColor="bg-info/10"
                iconColor="text-info"
              />
              <MetricCard
                title="Other"
                value={formatCurrency(breakdown.other)}
                subtitle={`${getPercentage(breakdown.other)}% of collected`}
                icon={CircleDollarSign}
                valueColor="text-muted-foreground"
                iconBgColor="bg-muted"
                iconColor="text-muted-foreground"
                onClick={() => setActivePaymentSheet('other')}
                className="cursor-pointer hover:border-primary/50 transition-colors"
              />
            </div>
          );
        })()}
      </div>

      {/* Breakdown Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RevenueBreakdown items={revenueBreakdownItems} />
        <CostBreakdown items={costBreakdownItems} />
      </div>

      <PaymentJobsSheet
        title="Other Payment Jobs"
        jobs={metrics?.otherJobs || []}
        open={activePaymentSheet === 'other'}
        onOpenChange={(open) => !open && setActivePaymentSheet(null)}
      />
    </div>
  );
};
